import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useSelfService } from "@/hooks/useSelfService";
import {
  SelfServiceForm,
  selfServiceSchema,
} from "@/schema/self-service.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, TriangleAlert } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "wouter";
import logo from "/asset/logo-removebg-preview.png";
import { Callout } from "@radix-ui/themes";
import LoadingIndicator from "@/components/LoadingIndicator";

const SelfServicePage = () => {
  const { toast } = useToast();
  const [_, navigate] = useLocation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const queryParams = new URLSearchParams(window.location.search);

  const businessId = queryParams.get("businessId");
  const branchId = queryParams.get("branchId");
  const invoiceId = queryParams.get("invoiceId");

  // Direct invoice URL: ?branchId=X&invoiceId=Y
  useEffect(() => {
    if (invoiceId) {
      navigate(`/self-service/${invoiceId}`);
    }
  }, []);

  const {
    findSelfServiceInvoiceAsync,
    isSearchingInvoice,
    // selfServiceBusinessAndBranch,
    selfServiceBusinessAndBranchIsLoading,
    selfServiceBusinessAndBranchIsError,
  } = useSelfService(undefined, businessId, branchId);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SelfServiceForm>({
    resolver: zodResolver(selfServiceSchema),
    defaultValues: {
      businessId: businessId ?? undefined,
      branchId: branchId ?? undefined,
      postReferenceNumber: "",
      invoiceDate: "",
      totalPayableAmount: undefined,
    },
  });

  if (!businessId && !branchId && !invoiceId) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center py-8 relative overflow-hidden bg-gradient-to-br from-blue-600 to-purple-700">
        <div className="absolute inset-0" style={{ opacity: 0.8 }}>
          <svg
            className="w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            role="img"
            aria-labelledby="waveTitle"
          >
            <title id="waveTitle">Animated wave background</title>
            <path
              fill="rgba(59, 130, 246, 0.2)"
              d="M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z"
            >
              <animate
                attributeName="d"
                dur="10s"
                repeatCount="indefinite"
                values="
                  M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z;
                  M-150 30 C-100 40, -50 50, 0 30 S 50 40, 100 50 S 150 40, 200 30 V100 H-150 Z;
                  M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z"
              />
            </path>
            <path
              fill="rgba(139, 92, 246, 0.2)"
              d="M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z"
            >
              <animate
                attributeName="d"
                dur="8s"
                repeatCount="indefinite"
                values="
                  M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z;
                  M-150 40 C-100 50, -50 60, 0 40 S 50 50, 100 60 S 150 50, 200 40 V100 H-150 Z;
                  M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z"
              />
            </path>
            <path
              fill="rgba(99, 102, 241, 0.2)"
              d="M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z"
            >
              <animate
                attributeName="d"
                dur="6s"
                repeatCount="indefinite"
                values="
                  M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z;
                  M-150 50 C-100 60, -50 70, 0 50 S 50 60, 100 70 S 150 60, 200 50 V100 H-150 Z;
                  M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z"
              />
            </path>
          </svg>
        </div>
        <Card className="w-4/5 lg:w-1/2 bg-white/90 backdrop-blur-sm shadow-xl relative z-10 p-4">
          <CardHeader className="gap-8">
            <CardTitle className="flex items-center justify-center">
              <img className="h-16" src={logo} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Callout.Root color="amber" className="mb-4">
              <Callout.Icon>
                <TriangleAlert />
              </Callout.Icon>
              <Callout.Text>
                <span className="font-semibold">This link is outdated.</span>
              </Callout.Text>
            </Callout.Root>
            <p className="text-sm text-gray-600">
              Please request an updated Self-Service link from your business.
              The new link can be obtained by going to the{" "}
              <span className="font-medium text-gray-800">Branch</span> menu and
              clicking the{" "}
              <span className="font-medium text-gray-800">
                Self-Service URL
              </span>{" "}
              button.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (
    businessId &&
    branchId &&
    (selfServiceBusinessAndBranchIsLoading ||
      selfServiceBusinessAndBranchIsError)
  ) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center py-8 relative overflow-hidden bg-gradient-to-br from-blue-600 to-purple-700">
        <div className="absolute inset-0" style={{ opacity: 0.8 }}>
          <svg
            className="w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            role="img"
            aria-labelledby="waveTitle"
          >
            <title id="waveTitle">Animated wave background</title>
            {/* Wave Group 1 */}
            <path
              fill="rgba(59, 130, 246, 0.2)"
              d="M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z"
              className="animate-wave-slow"
            >
              <animate
                attributeName="d"
                dur="10s"
                repeatCount="indefinite"
                values="
                  M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z;
                  M-150 30 C-100 40, -50 50, 0 30 S 50 40, 100 50 S 150 40, 200 30 V100 H-150 Z;
                  M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z"
              />
            </path>

            {/* Wave Group 2 */}
            <path
              fill="rgba(139, 92, 246, 0.2)"
              d="M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z"
              className="animate-wave-medium"
            >
              <animate
                attributeName="d"
                dur="8s"
                repeatCount="indefinite"
                values="
                  M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z;
                  M-150 40 C-100 50, -50 60, 0 40 S 50 50, 100 60 S 150 50, 200 40 V100 H-150 Z;
                  M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z"
              />
            </path>

            {/* Wave Group 3 */}
            <path
              fill="rgba(99, 102, 241, 0.2)"
              d="M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z"
              className="animate-wave-fast"
            >
              <animate
                attributeName="d"
                dur="6s"
                repeatCount="indefinite"
                values="
                  M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z;
                  M-150 50 C-100 60, -50 70, 0 50 S 50 60, 100 70 S 150 60, 200 50 V100 H-150 Z;
                  M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z"
              />
            </path>
          </svg>

          {/* Floating Particles */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={`particle-${i}-${Math.random().toString(36)}`}
                className="absolute rounded-full bg-white/10 animate-float"
                style={{
                  width: `${Math.random() * 10 + 5}px`,
                  height: `${Math.random() * 10 + 5}px`,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${Math.random() * 10 + 10}s`,
                }}
              />
            ))}
          </div>
        </div>
        <Card className="w-4/5 lg:w-1/2 bg-white/90 backdrop-blur-sm shadow-xl relative z-10 p-4">
          <CardHeader className="gap-8">
            <CardTitle className="flex items-center justify-center">
              <img
                className="h-16"
                // src="/logo-removebg-preview.png"
                src={logo}
              />
            </CardTitle>
            {/* <CardDescription>
              Invalid Link
            </CardDescription> */}
          </CardHeader>
          <CardContent>
            {selfServiceBusinessAndBranchIsLoading ? (
              <LoadingIndicator />
            ) : selfServiceBusinessAndBranchIsError ? (
              <>
                <h2 className="mt-6 mb-2 font-semibold text-xl text-red-600">
                  Invalid Link
                </h2>
                <Callout.Root color="red">
                  <Callout.Icon>
                    <TriangleAlert />
                  </Callout.Icon>
                  <Callout.Text>
                    This link is invalid, please contact your supplier to obtain
                    the correct link.
                  </Callout.Text>
                </Callout.Root>
              </>
            ) : (
              <>
                <h2 className="mt-6 mb-2 font-semibold text-xl text-red-600">
                  Something went wrong
                </h2>
                <Callout.Root color="red">
                  <Callout.Icon>
                    <TriangleAlert />
                  </Callout.Icon>
                  <Callout.Text>
                    Oops! Something went wrong, please try again later.
                  </Callout.Text>
                </Callout.Root>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const onSubmit = async (data: SelfServiceForm) => {
    await findSelfServiceInvoiceAsync(data, {
      onSuccess: (result) => {
        toast({
          title: "MySoft",
          description: "Welcome to MySoft Self Service Portal.",
          duration: 10000,
        });
        navigate(`/self-service/${result.data.id}`);
      },
      onError: (error: any) => {
        toast({
          title: "Failed",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "No invoice found. Please try again.",
          duration: 10000,
        });
      },
    });
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center py-8 relative overflow-hidden bg-gradient-to-br from-blue-600 to-purple-700">
      <div className="absolute inset-0" style={{ opacity: 0.8 }}>
        <svg
          className="w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          role="img"
          aria-labelledby="waveTitle"
        >
          <title id="waveTitle">Animated wave background</title>
          {/* Wave Group 1 */}
          <path
            fill="rgba(59, 130, 246, 0.2)"
            d="M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z"
            className="animate-wave-slow"
          >
            <animate
              attributeName="d"
              dur="10s"
              repeatCount="indefinite"
              values="
                M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z;
                M-150 30 C-100 40, -50 50, 0 30 S 50 40, 100 50 S 150 40, 200 30 V100 H-150 Z;
                M-150 40 C-100 50, -50 30, 0 40 S 50 50, 100 40 S 150 30, 200 40 V100 H-150 Z"
            />
          </path>

          {/* Wave Group 2 */}
          <path
            fill="rgba(139, 92, 246, 0.2)"
            d="M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z"
            className="animate-wave-medium"
          >
            <animate
              attributeName="d"
              dur="8s"
              repeatCount="indefinite"
              values="
                M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z;
                M-150 40 C-100 50, -50 60, 0 40 S 50 50, 100 60 S 150 50, 200 40 V100 H-150 Z;
                M-150 50 C-100 60, -50 40, 0 50 S 50 60, 100 50 S 150 40, 200 50 V100 H-150 Z"
            />
          </path>

          {/* Wave Group 3 */}
          <path
            fill="rgba(99, 102, 241, 0.2)"
            d="M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z"
            className="animate-wave-fast"
          >
            <animate
              attributeName="d"
              dur="6s"
              repeatCount="indefinite"
              values="
                M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z;
                M-150 50 C-100 60, -50 70, 0 50 S 50 60, 100 70 S 150 60, 200 50 V100 H-150 Z;
                M-150 60 C-100 70, -50 50, 0 60 S 50 70, 100 60 S 150 50, 200 60 V100 H-150 Z"
            />
          </path>
        </svg>

        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={`particle-${i}-${Math.random().toString(36)}`}
              className="absolute rounded-full bg-white/10 animate-float"
              style={{
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 10 + 10}s`,
              }}
            />
          ))}
        </div>
      </div>
      <Card className="w-4/5 lg:w-1/2 bg-white/90 backdrop-blur-sm shadow-xl relative z-10 p-8">
        <CardHeader className="gap-8">
          <CardTitle className="flex items-center justify-center">
            <img
              className="h-16"
              // src="/logo-removebg-preview.png"
              src={logo}
            />
          </CardTitle>
          <CardDescription>
            Fill in the required details to find your invoice.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="w-full flex flex-col gap-5"
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* <div>
              <Label htmlFor="businessBrn" required>
                Business BRN
              </Label>
              <Input
                id="businessBrn"
                {...register("businessBrn")}
                errors={!!errors.businessBrn}
              />
              {errors.businessBrn && (
                <p className="text-red-600 text-sm">
                  {errors.businessBrn.message}
                </p>
              )}
            </div> */}
            <div>
              <Label htmlFor="PostReferenceNumber" required>
                POS Reference Number
              </Label>
              <Input
                id="postReferenceNumber"
                {...register("postReferenceNumber")}
                errors={!!errors.postReferenceNumber}
              />
              {errors.postReferenceNumber && (
                <p className="text-red-600 text-sm">
                  {errors.postReferenceNumber.message}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="invoiceDate" required>
                Invoice Date
              </Label>
              <Input
                id="invoiceDate"
                type="date"
                {...register("invoiceDate")}
                errors={!!errors.invoiceDate}
              />
              {errors.invoiceDate && (
                <p className="text-red-600 text-sm">
                  {errors.invoiceDate.message}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="totalPayableAmount" required>
                Invoice Total Amount
              </Label>
              <Input
                id="totalPayableAmount"
                {...register("totalPayableAmount", { valueAsNumber: true })}
                errors={!!errors.totalPayableAmount}
              />
              {errors.totalPayableAmount && (
                <p className="text-red-600 text-sm">
                  {errors.totalPayableAmount.message}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2.5 text-sm">
              <Input required className="size-4" type="checkbox" />
              <p>
                I hereby agree with the{" "}
                <a
                  className="hover:underline text-blue-500 cursor-pointer"
                  onClick={() => setIsOpen(true)}
                >
                  terms and conditions
                </a>
                .
              </p>
            </div>
            <CardFooter className="pt-4 pb-0">
              <div className="w-full flex items-center justify-center">
                <Button type="submit" disabled={isSearchingInvoice}>
                  {isSearchingInvoice ? (
                    <>
                      <Loader2
                        size={10}
                        className="animate-spin text-primary"
                      />
                      Searching
                    </>
                  ) : (
                    "Search"
                  )}
                </Button>
              </div>
            </CardFooter>
          </form>
          <TermAndCondition isOpen={isOpen} setIsOpen={setIsOpen} />
        </CardContent>
      </Card>
    </div>
  );
};

type TermsAndConditionProp = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};
const TermAndCondition = ({ isOpen, setIsOpen }: TermsAndConditionProp) => {
  return (
    <Dialog open={isOpen} onOpenChange={() => setIsOpen(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Terms and Conditions</DialogTitle>
        </DialogHeader>
        <p className="text-[14px] text-justify">
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse
          imperdiet justo sit amet feugiat aliquam. Curabitur tincidunt feugiat
          quam at porta. In pulvinar, arcu ac dapibus finibus, lorem mi dapibus
          lectus, eu porta ex magna id ligula. Integer imperdiet faucibus
          suscipit. Nullam a sem diam. Nam bibendum mollis massa, ut mollis odio
          luctus non. Nullam sit amet lacus maximus, ultrices velit nec, congue
          eros. Donec tincidunt tortor arcu, quis bibendum eros facilisis vitae.
          Sed dictum luctus metus, in blandit dui mattis quis. Ut in accumsan
          velit, quis lacinia justo. Aliquam ultrices luctus urna et
          condimentum.
        </p>
        <DialogFooter>
          <Button size="sm" type="button" onClick={() => setIsOpen(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SelfServicePage;
